import {gsap, ScrollTrigger} from "../LibrariesImport";
import parseScrub from "../utils/parseScrub";
import parseVars from "../utils/parseVars";

/**
 * DataAnim — bibliothèque d'animations pilotées par attributs (data-anim-*).
 * Remplace DataAppearOnScroll + DataBatch + DataParallax + DataRotate.
 *
 * ── Famille RÉVÉLATION ────────────────────────────────────────────────────────
 *   Types : fade-up | fade-left | fade-right | opacity | zoom-in | zoom-out
 *   Modes : one-shot (défaut) | scrub (data-anim-scrub) | batch (data-anim-batch)
 *
 * ── Famille PARALLAX ──────────────────────────────────────────────────────────
 *   Types : parallax | parallax-inside | rotate
 *   Jamais masqués — transformation continue calée au scroll (scrub par défaut).
 *     - parallax        : translation (offset via data-anim-to, ex "y:-30px").
 *     - parallax-inside : défilement interne d'une image dans son [data-anim-clip].
 *     - rotate          : rotation via --data-rotate (data-anim-from/to en deg,
 *                         défauts "0deg" -> "180deg").
 *
 * -----------------------------------------------------------------------------
 * API (attributs HTML) — tout est sous le namespace `data-anim-*`
 * -----------------------------------------------------------------------------
 * | Attribut                  | Rôle                                      | Familles         |
 * |---------------------------|-------------------------------------------|------------------|
 * | data-anim="<type>"         | type d'anim — REQUIS (sans valeur : non animé) | tous        |
 * | data-anim-delay            | délai (s) avant l'anim                    | one-shot/batch   |
 * | data-anim-duration         | durée (s), défaut 0.85                     | one-shot/batch   |
 * | data-anim-ease             | easing GSAP, défaut "power1.out"          | révélation/parallax |
 * | data-anim-from             | surcouche des vars de DÉPART (GSAP)       | tous             |
 * | data-anim-to               | vars d'ARRIVÉE (révélation) / offset (parallax) | révélation/parallax |
 * | data-anim-from-above       | vars de DÉPART supplémentaires AU-DESSUS du breakpoint | révélation |
 * | data-anim-to-above         | vars d'ARRIVÉE supplémentaires AU-DESSUS du breakpoint | révélation |
 * | data-anim-from-below       | vars de DÉPART supplémentaires EN-DESSOUS du breakpoint | révélation |
 * | data-anim-to-below         | vars d'ARRIVÉE supplémentaires EN-DESSOUS du breakpoint | révélation |
 * | data-anim-start            | position de déclenchement ScrollTrigger   | tous             |
 * | data-anim-end              | position de fin ScrollTrigger             | scrub/parallax   |
 * | data-anim-breakpoint="md"  | seuil : sans vars de côté → n'anime qu'au-dessus ; avec vars *-above/-below → anime partout, en superposant chaque côté | tous |
 * | data-anim-scrub[="1"]      | active/règle le scrub (lissage)           | -> scrub / parallax/rotate (réglage) |
 * | data-anim-batch[="nom"]    | active le mode batch (valeur = groupe)    | -> batch         |
 * | data-anim-stagger          | pas du stagger (s) en batch, défaut 0.1   | batch            |
 * | data-anim-inside           | ratio de débord (%), défaut 20            | parallax-inside  |
 * | data-anim-clip             | (sur le PARENT) cadre qui clippe l'image  | parallax-inside  |
 * | data-anim-trigger          | élément déclencheur : "parent" ou un sélecteur CSS | parallax |
 *
 * -----------------------------------------------------------------------------
 * EXEMPLES
 * -----------------------------------------------------------------------------
 *   <div data-anim="fade-up">                                  one-shot fade-up
 *   <div data-anim="zoom-in" data-anim-delay="0.2">
 *   <div data-anim="fade-up" data-anim-scrub data-anim-to="rotate:2deg">  scrub + rotation
 *   <div data-anim="fade-up" data-anim-batch="stats">          membre du groupe "stats"
 *   <div data-anim="fade-up" data-anim-breakpoint="lg">        anime seulement en >= lg
 *   <div data-anim="fade-up" data-anim-breakpoint="md"        fade-up partout, + rotation dès md
 *        data-anim-from-above="rotate:8deg" data-anim-to-above="rotate:0deg">
 *   <h1  data-anim="parallax" data-anim-to="y:-30px">          translation parallax
 *   <div data-anim="parallax" data-anim-to="y:-80px" data-anim-trigger="parent">  calé sur le parent
 *   <div data-anim="parallax" data-anim-to="y:-40px" data-anim-trigger=".section"> calé sur un ancêtre
 *   <span data-anim="rotate">                                  rotation 0deg -> 180deg au scroll
 *   <span data-anim="rotate" data-anim-from="0deg" data-anim-to="360deg" data-anim-scrub="1">
 *   <div data-anim-clip>                                       cadre (parent)
 *       <img data-anim="parallax-inside" data-anim-inside="20"> défilement interne
 *   </div>
 *
 *   -----------------------------------------------------------------------------
 * ACCESSIBILITÉ (prefers-reduced-motion)
 * -----------------------------------------------------------------------------
 * En reduced-motion : pas de scrub, pas de batch, pas de parallax, pas de
 * rotate, pas de mouvement. Les révélations deviennent un simple fondu
 * (autoAlpha 0 -> 1) ; parallax et rotate sont neutralisés (élément laissé
 * naturel). Conforme RGAA/WCAG.
 */


const FROM_VARS = {
    "fade-up":    {y: 50,    autoAlpha: 0},
    "fade-left":  {x: -50,   autoAlpha: 0},
    "fade-right": {x: 50,    autoAlpha: 0},
    "opacity":    {autoAlpha: 0},
    "zoom-in":    {scale: 0.9, autoAlpha: 0},
    "zoom-out":   {scale: 1.1, autoAlpha: 0},
};

const TO_VARS = {autoAlpha: 1, x: 0, y: 0, scale: 1};

const DEFAULTS = {duration: 0.85, ease: "power1.out", stagger: 0.1};

const CONTINUOUS = {start: "clamp(top bottom)", end: "clamp(bottom top)"};

// Seuil des révélations one-shot / batch. clamp() ramène le déclenchement au scroll max de la
// page : sans lui, un élément posé dans les derniers 10 % du document (bas du footer) n'atteint
// jamais les 90 % du viewport et reste masqué.
const REVEAL_START = "clamp(top 90%)";

const SIZES = {sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400, hyper: 1800};

const SIDE_VARS = ["animFromAbove", "animToAbove", "animFromBelow", "animToBelow"];

const RELEVANT = {
    scrub:    ["anim", "animFrom", "animTo", ...SIDE_VARS, "animStart", "animEnd", "animScrub", "animEase", "animBreakpoint"],
    batch:    ["anim", "animFrom", "animTo", ...SIDE_VARS, "animStart", "animBatch", "animDelay", "animDuration", "animEase", "animStagger", "animBreakpoint"],
    oneshot:  ["anim", "animFrom", "animTo", ...SIDE_VARS, "animStart", "animDelay", "animDuration", "animEase", "animBreakpoint"],
    parallax: ["anim", "animFrom", "animTo", "animStart", "animEnd", "animEase", "animScrub", "animTrigger", "animBreakpoint"],
    inside:   ["anim", "animInside", "animStart", "animEnd", "animBreakpoint"],
    rotate:   ["anim", "animFrom", "animTo", "animScrub", "animStart", "animEnd", "animEase", "animBreakpoint"],
};

export default class DataAnim {
    constructor() {
        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.matchMedia = gsap.matchMedia();

        const batches = new Map();

        document.querySelectorAll("[data-anim]").forEach((el) => {
            this.lint(el);

            switch (this.resolveMode(el)) {
                case "parallax":
                    this.withBreakpoint(el, () => this.parallaxMove(el), () => {});
                    break;
                case "inside":
                    this.withBreakpoint(el, () => this.parallaxInside(el), () => {});
                    break;
                case "rotate":
                    this.withBreakpoint(el, () => this.rotateScroll(el), () => {});
                    break;
                case "scrub":
                    this.withBreakpoint(el, (above) => this.scrubReveal(el, above));
                    break;
                case "batch": {
                    const name = el.dataset.animBatch || "default";
                    if (!batches.has(name)) batches.set(name, []);
                    batches.get(name).push(el);
                    break;
                }
                case "none":
                case "inert":
                    break;
                default:
                    this.withBreakpoint(el, (above) => this.oneShotReveal(el, above));
            }
        });

        batches.forEach((els) => this.batchReveal(els));
    }

    intendedMode(el) {
        const type = el.dataset.anim;
        if (type === "parallax-inside") return "inside";
        if (type === "parallax") return "parallax";
        if (type === "rotate") return "rotate";
        if (!FROM_VARS[type]) return "inert";
        if (el.dataset.animScrub !== undefined) return "scrub";
        if (el.dataset.animBatch !== undefined) return "batch";
        return "oneshot";
    }

    resolveMode(el) {
        const intended = this.intendedMode(el);
        if (intended === "inert") return "inert";
        if (!this.reducedMotion) return intended;
        return (intended === "parallax" || intended === "inside" || intended === "rotate") ? "none" : "oneshot";
    }

    /**
     * `above` = true quand on est au-dessus de data-anim-breakpoint (ou qu'il n'y
     * en a pas). Les vars de côté (data-anim-from-above / -below, data-anim-to-above
     * / -below) se superposent aux vars de base du côté correspondant du seuil :
     * from/to gardent leur sens (départ / arrivée), below/above désignent juste le
     * côté du breakpoint.
     */
    vars(el, above = true) {
        const type     = el.dataset.anim;
        const sideFrom = above ? el.dataset.animFromAbove : el.dataset.animFromBelow;
        const sideTo   = above ? el.dataset.animToAbove   : el.dataset.animToBelow;

        const fromVars = this.reducedMotion ?
            {autoAlpha: 0} :
            {
                ...FROM_VARS[type],
                ...parseVars(el.dataset.animFrom),
                ...parseVars(sideFrom),
            };
        const toVars = {
            ...TO_VARS,
            ...parseVars(el.dataset.animTo),
            ...parseVars(sideTo),
        };
        return {fromVars, toVars};
    }

    hasResponsiveVars(el) {
        return el.dataset.animFromAbove !== undefined
            || el.dataset.animToAbove   !== undefined
            || el.dataset.animFromBelow !== undefined
            || el.dataset.animToBelow   !== undefined;
    }

    timing(el) {
        return {
            delay:    parseFloat(el.dataset.animDelay ?? 0),
            duration: this.reducedMotion ? 1 : parseFloat(el.dataset.animDuration ?? DEFAULTS.duration),
            ease:     el.dataset.animEase || DEFAULTS.ease,
        };
    }

    withBreakpoint(el, run, staticFn) {
        const px = SIZES[el.dataset.animBreakpoint];
        if (!px) {
            run(true);
            return;
        }
        const responsive = this.hasResponsiveVars(el);
        this.matchMedia.add(
            {enabled: `(min-width: ${px}px)`, disabled: `(max-width: ${px - 0.02}px)`},
            (ctx) => {
                if (ctx.conditions.enabled) {
                    run(true);
                } else if (responsive) {
                    // Vars de côté : on anime quand même, avec les vars *-below.
                    run(false);
                } else if (staticFn) {
                    staticFn();
                } else {
                    gsap.set(el, this.vars(el, false).toVars);
                }
            }
        );
    }

    scrubReveal(el, above = true) {
        const {fromVars, toVars} = this.vars(el, above);
        gsap.fromTo(el, fromVars, {
            ...toVars,
            ease: el.dataset.animEase || "power1.out",
            scrollTrigger: {
                trigger: el,
                start:  el.dataset.animStart ?? "top bottom",
                end:    el.dataset.animEnd   ?? "top 60%",
                scrub:  parseScrub(el.dataset.animScrub),
            },
        });
    }

    oneShotReveal(el, above = true) {
        const {fromVars, toVars} = this.vars(el, above);
        const {delay, duration, ease} = this.timing(el);
        gsap.fromTo(el, {...fromVars, willChange: "transform, opacity"}, {
            ...toVars,
            delay,
            duration,
            ease,
            clearProps: "willChange",
            scrollTrigger: {trigger: el, start: el.dataset.animStart ?? REVEAL_START, once: true},
        });
    }

    batchReveal(els) {
        const breakpoints = [...new Set(els.map((el) => el.dataset.animBreakpoint).filter(Boolean))];
        if (breakpoints.length > 1) {
            console.warn(
                `[data-anim] groupe batch « ${els[0].dataset.animBatch || "default"} » : breakpoints mélangés (${breakpoints.join(", ")}). Le premier (${breakpoints[0]}) est appliqué à tout le groupe.`,
                els
            );
        }

        const px = SIZES[breakpoints[0]];

        if (!px) { this.runBatch(els, true); return; }

        const responsive = els.some((el) => this.hasResponsiveVars(el));

        this.matchMedia.add(
            {enabled: `(min-width: ${px}px)`, disabled: `(max-width: ${px - 0.02}px)`},
            (ctx) => {
                if (ctx.conditions.enabled) this.runBatch(els, true);
                else if (responsive) this.runBatch(els, false);
                else els.forEach((el) => gsap.set(el, this.vars(el, false).toVars));
            }
        );
    }

    runBatch(els, above = true) {
        const start = els.find((el) => el.dataset.animStart)?.dataset.animStart ?? REVEAL_START;
        const step  = parseFloat(els.find((el) => el.dataset.animStagger)?.dataset.animStagger ?? DEFAULTS.stagger);

        ScrollTrigger.batch(els, {
            start,
            once: true,
            onEnter: (batch) =>
                batch.forEach((el, i) => {
                    const {fromVars, toVars} = this.vars(el, above);
                    const {duration, ease}   = this.timing(el);
                    gsap.fromTo(el, {...fromVars, willChange: "transform, opacity"}, {
                        ...toVars,
                        duration,
                        delay: parseFloat(el.dataset.animDelay ?? 0) + i * step,
                        ease,
                        clearProps: "willChange",
                    });
                }),
        });
    }

    parallaxMove(el) {
        const triggerAttr = el.dataset.animTrigger;
        const trigger = triggerAttr === "parent"
            ? el.parentElement
            : triggerAttr
                ? el.closest(triggerAttr) || el
                : el;
        const scrub = el.dataset.animScrub !== undefined ? parseScrub(el.dataset.animScrub) : true;

        gsap.fromTo(
            el,
            {x: 0, y: 0, ...parseVars(el.dataset.animFrom)},
            {
                x: 0, y: 0,
                ...parseVars(el.dataset.animTo),
                ease: el.dataset.animEase || "none",
                scrollTrigger: {
                    trigger,
                    start:   el.dataset.animStart ?? CONTINUOUS.start,
                    end:     el.dataset.animEnd   ?? CONTINUOUS.end,
                    scrub,
                },
            }
        );
    }

    parallaxInside(el) {
        const ratio = parseFloat(el.dataset.animInside || 20);
        gsap.fromTo(
            el,
            {scale: 1 + ratio / 100, yPercent: -ratio / 2},
            {
                yPercent: ratio / 2,
                ease: "none",
                scrollTrigger: {
                    trigger: el.closest("[data-anim-clip]") || el,
                    start:   el.dataset.animStart ?? CONTINUOUS.start,
                    end:     el.dataset.animEnd   ?? CONTINUOUS.end,
                    scrub:   true,
                },
            }
        );
    }

    rotateScroll(el) {
        const from  = el.dataset.animFrom ?? "0deg";
        const to    = el.dataset.animTo   ?? "180deg";
        const scrub = el.dataset.animScrub !== undefined ? parseScrub(el.dataset.animScrub) : true;
        gsap.fromTo(
            el,
            {"--data-rotate": from},
            {
                "--data-rotate": to,
                ease: el.dataset.animEase || "none",
                scrollTrigger: {
                    trigger: el,
                    start:   el.dataset.animStart ?? CONTINUOUS.start,
                    end:     el.dataset.animEnd   ?? CONTINUOUS.end,
                    scrub,
                },
            }
        );
    }

    lint(el) {
        const mode      = this.intendedMode(el);
        const type      = el.dataset.anim;
        const breakpoint = el.dataset.animBreakpoint;
        const issues    = [];

        if (mode === "inert") {
            issues.push(type ?
                `type inconnu « ${type} » → élément non animé` :
                "data-anim sans valeur → élément non animé");
        }
        if (breakpoint !== undefined && !SIZES[breakpoint]) {
            issues.push(`breakpoint inconnu « ${breakpoint} » (attendus : ${Object.keys(SIZES).join(", ")})`);
        }
        if (this.hasResponsiveVars(el) && !SIZES[breakpoint]) {
            issues.push("vars de côté (data-anim-*-above / -below) sans data-anim-breakpoint valide → aucun seuil, vars ignorées");
        }
        if (mode === "inside" && !el.closest("[data-anim-clip]")) {
            issues.push("parallax-inside sans parent [data-anim-clip] (débord non masqué)");
        }
        if (mode === "parallax" && !el.dataset.animTo && !el.dataset.animFrom) {
            issues.push("parallax sans data-anim-to ni data-anim-from (aucun déplacement)");
        }
        if (RELEVANT[mode]) {
            const extras = Object.keys(el.dataset).filter((key) => key.startsWith("anim") && !RELEVANT[mode].includes(key));
            if (extras.length) {
                const attrs = extras.map((key) => "data-" + key.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase()));
                issues.push(`attribut(s) sans effet en mode « ${mode} » : ${attrs.join(", ")}`);
            }
        }

        if (issues.length) {
            console.warn(`[data-anim] ${issues.join(" — ")}. À corriger.`, el);
        }
    }
}